import React from 'react'

interface ResolveBackgroundParams {
  /**
   * `theme.layout.background`. Historically an image URL; also accepts any CSS
   * background value (gradients included), which is why it is sniffed rather
   * than assumed.
   */
  background?: string
  backgroundColor?: string
  /** `set.images.background` — always an image URL. */
  sceneImage?: string
  /** A video background replaces any image. */
  hasVideo?: boolean
}

const IMAGE_EXTENSION = /\.(webp|png|jpe?g|gif|svg|avif)(\?.*)?$/i

/**
 * A bare image source: absolute, protocol-relative, root-relative, a `data:`
 * URI, or a relative path ending in an image extension (`images/bg.webp`).
 * Anything else — `linear-gradient(...)`, a shorthand, a color — is passed
 * through to CSS untouched.
 *
 * The relative and `data:` cases matter because the previous code wrapped
 * *every* value in `url(...)`: classifying them as CSS would emit
 * `background: images/bg.webp`, which the browser discards, losing a background
 * that used to render.
 */
const isImageUrl = (value: string): boolean =>
  /^(https?:)?\/\//.test(value) ||
  value.startsWith('/') ||
  value.startsWith('data:') ||
  (!value.includes(' ') && IMAGE_EXTENSION.test(value))

/**
 * Builds the scene background.
 *
 * The old version assigned `background` and then unconditionally overwrote
 * `backgroundImage` with `url(<same value>)`. A gradient therefore became
 * `url(linear-gradient(…))` — an invalid image request that dropped the
 * gradient the config asked for — and an unset background emitted `url()`,
 * which browsers resolve against the current page.
 */
export const resolveBackground = ({
  background,
  backgroundColor,
  sceneImage,
  hasVideo = false,
}: ResolveBackgroundParams): React.CSSProperties => {
  const value = background?.trim()
  const isUrl = !!value && isImageUrl(value)
  const isCssValue = !!value && !isUrl

  // A themed CSS value replaces the scene image rather than layering under it:
  // `backgroundImage` is emitted after the shorthand and would overwrite the
  // gradient's own image layer, silently dropping the themed background.
  const imageSrc = isUrl ? value : (isCssValue ? undefined : sceneImage?.trim())

  const style: React.CSSProperties = {
    // Any non-URL value is a raw CSS background (gradient, shorthand, color).
    ...(isCssValue ? { background: value } : {}),
  }

  if (hasVideo) {
    style.backgroundImage = 'none'
  } else if (imageSrc) {
    style.backgroundImage = `url(${imageSrc})`
    style.backgroundSize = 'cover'
    style.backgroundPosition = 'center'
  }

  // Set last so it wins over the color slot of a `background` shorthand. A
  // gradient lives in the image slot and survives this.
  if (backgroundColor) {
    style.backgroundColor = backgroundColor
  }

  return style
}
